import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, FileDown, Send, PackageCheck } from "lucide-react";
import { api, API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { POStatusBadge } from "@/components/POStatusBadge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PO_STATUSES, PO_STATUS_LABEL, WAREHOUSE_LABEL } from "@/lib/constants";
import { formatCurrency, formatDate, formatDateTime } from "@/lib/format";

export default function BestellingDetail() {
  const { id } = useParams();
  const [po, setPo] = useState(null);

  const load = async () => {
    const r = await api.get(`/purchase-orders/${id}`);
    setPo(r.data);
  };
  useEffect(() => { load(); }, [id]);
  if (!po) return <div className="px-8 py-12">Laden…</div>;

  const approve = async () => {
    try {
      const r = await api.post(`/purchase-orders/${po.id}/approve`);
      toast.success("Bestelling goedgekeurd & e-mail verzonden (GEMOCKT)", { description: r.data.message });
      load();
    } catch (e) { toast.error("Goedkeuring mislukt", { description: e.message }); }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/purchase-orders/${po.id}/status`, { status: newStatus });
      toast.success(`Status bijgewerkt → ${PO_STATUS_LABEL[newStatus]}`);
      load();
    } catch (e) { toast.error("Bijwerken mislukt"); }
  };

  const canReceive = ["approved", "ordered", "production_planned", "in_production",
                      "ready", "shipped", "in_transit"].includes(po.status);

  return (
    <div className="px-4 md:px-8 lg:px-12 py-8 md:py-10" data-testid="po-detail-page">
      <Link to="/bestellingen" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6">
        <ArrowLeft className="w-4 h-4" /> Terug naar bestellingen
      </Link>

      <PageHeader eyebrow={po.supplier_name} title={po.po_number}
        description={`${po.lines?.length || 0} orderregels · ${formatCurrency(po.total_value)} · Ontvangst in ${WAREHOUSE_LABEL[po.receive_warehouse]}`}
        action={
          <div className="flex flex-wrap items-center gap-2">
            <a data-testid="download-pdf" href={`${API}/purchase-orders/${po.id}/pdf`} target="_blank" rel="noreferrer">
              <Button variant="outline" className="touch-tap"><FileDown className="w-4 h-4 mr-2" /> PDF downloaden</Button>
            </a>
            {po.status === "draft" && (
              <Button data-testid="approve-po" onClick={approve} className="touch-tap">
                <Send className="w-4 h-4 mr-2" /> Goedkeuren & verzenden
              </Button>
            )}
            {canReceive && (
              <Link to={`/leveringen/${po.id}`}>
                <Button variant="outline" className="touch-tap" data-testid="goto-receipt">
                  <PackageCheck className="w-4 h-4 mr-2" /> Levering ontvangen
                </Button>
              </Link>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="surface overflow-hidden">
            <div className="p-6 border-b border-border flex items-center justify-between">
              <div>
                <div className="label-eyebrow">Orderregels</div>
                <h3 className="text-xl font-semibold mt-1">Producten</h3>
              </div>
              <div className="font-mono text-2xl font-semibold">{formatCurrency(po.total_value)}</div>
            </div>
            <table className="w-full text-base">
              <thead className="bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-6 py-3">Artikel</th>
                  <th className="px-6 py-3">Product</th>
                  <th className="px-6 py-3 text-right">Aantal</th>
                  <th className="px-6 py-3">Eenh.</th>
                  <th className="px-6 py-3 text-right">Prijs</th>
                  <th className="px-6 py-3 text-right">Subtotaal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {po.lines.map((l, idx) => (
                  <tr key={`${l.product_id}-${idx}`}>
                    <td className="px-6 py-4 font-mono text-sm">{l.article_number}</td>
                    <td className="px-6 py-4">{l.product_name}</td>
                    <td className="px-6 py-4 text-right font-mono">{l.quantity}</td>
                    <td className="px-6 py-4">{l.unit}</td>
                    <td className="px-6 py-4 text-right font-mono">{formatCurrency(l.purchase_price)}</td>
                    <td className="px-6 py-4 text-right font-mono font-semibold">{formatCurrency(l.quantity * l.purchase_price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="surface p-6" data-testid="email-history-section">
            <h3 className="text-xl font-semibold mb-4">E-mail geschiedenis</h3>
            {po.emails?.length === 0 && (
              <div className="text-sm text-muted-foreground">Nog geen e-mails verzonden. Keur de bestelling goed om te verzenden.</div>
            )}
            <div className="space-y-3">
              {po.emails?.map((e) => (
                <div key={e.id} className="border border-border rounded-md p-4">
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{e.subject}</div>
                    <span className="text-xs text-muted-foreground font-mono">{formatDateTime(e.sent_at)}</span>
                  </div>
                  <div className="mt-1 text-sm text-muted-foreground">Aan: {e.supplier_email}</div>
                  <pre className="mt-3 text-xs whitespace-pre-wrap font-sans text-muted-foreground">{e.body}</pre>
                  <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-status-low">GEMOCKT — Demomodus</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <div className="surface p-6">
            <div className="label-eyebrow mb-2">Status</div>
            <div className="flex items-center gap-3 mb-4"><POStatusBadge status={po.status} /></div>
            <Select value={po.status} onValueChange={updateStatus}>
              <SelectTrigger className="h-12" data-testid="status-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PO_STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`status-option-${s}`}>{PO_STATUS_LABEL[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="surface p-6 space-y-3 text-sm">
            <Detail label="Ordernummer" value={po.po_number} mono />
            <Detail label="Leverancier" value={po.supplier_name} />
            <Detail label="Contactpersoon" value={po.supplier?.contact_person} />
            <Detail label="E-mail" value={po.supplier?.email} />
            <Detail label="Besteldatum" value={formatDate(po.order_date)} />
            <Detail label="Productiedatum" value={formatDate(po.production_date)} />
            <Detail label="Verwachte levering" value={formatDate(po.expected_delivery_date)} />
            <Detail label="Ontvangst in" value={WAREHOUSE_LABEL[po.receive_warehouse]} />
            <Detail label="Opmerkingen" value={po.notes || "—"} />
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border last:border-0 pb-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={mono ? "font-mono font-medium" : "font-medium"}>{value || "—"}</span>
    </div>
  );
}
